import { Check, Copy } from "lucide-react";
import { ReactNode, useEffect } from "react";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgLink } from "@intelligems/ig-theme/IgLink";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { useCopyToClipboard } from "@intelligems/ig-theme/hooks/use-copy-to-clipboard";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import {
  useGetOrg,
  useGetOrgId,
} from "@src/api/organizations/get-organization";
import {
  refreshScriptStatus,
  useGetScriptStatus,
} from "@src/api/shops/get-script-status";
import { StepCardsContainer } from "@src/components/Dialogs/StepCardsContainer";
import { useScriptVerification } from "@src/hooks/useScriptVerification";
import { SaveChanges } from "@src/images/Icons/EnableIntelligems/SaveChanges";
import { ThemeLiquidFile } from "@src/images/Icons/EnableIntelligems/ThemeLiquidFile";
import { ToggleOnScript } from "@src/images/Icons/EnableIntelligems/ToggleOnScript";
import { contactSupport } from "@src/utils/contactSupport";
import { generateShopifyEditorUrl } from "@src/utils/generateThemeEditorLink";
import { FullIgScript } from "@src/utils/intelligemsThemeSnippets";
import { allIntegrationGuides, igDirectScriptAdd } from "@src/utils/linkSheet";
import { mpScriptIntegration } from "@src/utils/mixpanel";
import { openNewTab } from "@src/utils/openNewTab";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

const SHOW_SKIP_DELAY_MS = 50_000;

interface ConnectToShopifyContentProps {
  onContinue: () => void;
  onSkip?: () => void;
  successContent?: ReactNode;
  screenName?: string;
  onScriptVerified?: () => void;
  showFailureState?: boolean;
  // Overrides the Continue button's enablement when the caller has a truer
  // signal than the local `verified` latch (which can't go stale). Onboarding
  // passes its server-derived phase here so Continue tracks navigation instead
  // of a latch that drifts; other callers omit it and keep the latch behavior.
  canContinue?: boolean;
}

export const ConnectToShopifyContent = ({
  onContinue,
  onSkip,
  successContent,
  screenName = "Connect App embed page",
  onScriptVerified,
  showFailureState = true,
  canContinue,
}: ConnectToShopifyContentProps) => {
  const theme = useTheme();
  const org = useGetOrg();
  const orgId = useGetOrgId();
  const renderBlocking =
    useConfiguration<boolean>("enable_render_blocking")?.value ?? false;
  const scriptStatusQuery = useGetScriptStatus(
    org.data?.org.id,
    undefined,
    undefined,
    "quick"
  );
  const queryClient = useQueryClient();

  const {
    verified: scriptVerified,
    failed: scriptFailed,
    checking: checkedScript,
    triggerCheck,
    resetFailure,
  } = useScriptVerification(scriptStatusQuery, {
    autoDetect: true,
    showFailureState,
    onVerified: onScriptVerified,
    onCheckSuccess: () => toast.success("Script connected successfully!"),
    onCheckFailure: () =>
      toast.error("We did not detect the script on your store yet."),
  });

  const [openedShopify, setOpenedShopify] = useImmer(true);
  const [showManualSteps, setShowManualSteps] = useImmer(false);
  const { copied, copy } = useCopyToClipboard();
  const [showSkip, setShowSkip] = useImmer(false);

  useEffect(() => {
    if (!onSkip) {
      return;
    }
    const timer = setTimeout(() => setShowSkip(true), SHOW_SKIP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [onSkip, setShowSkip]);

  const currentScreenName = showManualSteps
    ? "Connect Script page"
    : screenName;

  const handleOpenShopify = (shopId?: string | undefined | null) => {
    setOpenedShopify(true);
    if (shopId) {
      openNewTab(
        `${org.data.org.shopId}/admin/themes/current/editor?context=apps&activateAppId=${
          import.meta.env.VITE_SHOPIFY_THEME_BLOCKS_UUID
        }/intelligems-script`,
        { noUtmParams: true }
      );
    }
  };

  const handleTestConnection = () => {
    mpScriptIntegration(currentScreenName, "Test script");
    triggerCheck();
    refreshScriptStatus(queryClient, org.data?.org.id, { mode: "quick" });
  };

  const handleCopyScript = () =>
    copy(FullIgScript(orgId || "", renderBlocking));

  const handleToggleManualSteps = () => {
    const action = showManualSteps ? "Back" : "Add script manually";
    mpScriptIntegration(currentScreenName, action);
    setShowManualSteps(!showManualSteps);
  };

  const handleRetest = () => {
    mpScriptIntegration(currentScreenName, "Retest the script");
    resetFailure();
    triggerCheck();
    refreshScriptStatus(queryClient, org.data?.org.id);
  };

  const handleTryAnotherWay = () => {
    mpScriptIntegration(currentScreenName, "Try another way");
    resetFailure();
    setShowManualSteps(!showManualSteps);
  };

  const handleContinue = () => {
    // Walkthrough callers enable Continue regardless of the script, so the
    // status is what separates a real completion from a skipped-past one.
    mpScriptIntegration(
      currentScreenName,
      "Continue",
      scriptVerified ? "verified" : "unverified"
    );
    onContinue();
  };

  if (scriptVerified && successContent) {
    return <>{successContent}</>;
  }

  const continueEnabled = canContinue ?? scriptVerified;

  const displaySkip = onSkip && (showSkip || scriptFailed);

  const testConnectionButton = scriptVerified
    ? {
        text: "Connected",
        variant: "outlined" as const,
        disabled: true,
        startIcon: <Check size={16} />,
        endIcon: null as ReactNode,
        onButtonClick: () => {},
        color: "secondary" as const,
      }
    : {
        text: checkedScript ? "Testing..." : "Test connection",
        disabled: !openedShopify || checkedScript,
        onButtonClick: handleTestConnection,
        color: "secondary" as const,
      };

  const autoFlowSteps = [
    {
      title: "Open Shopify admin",
      description: "Keep both windows open.",
      button: {
        text: "Open Shopify",
        color: "primary" as const,
        onButtonClick: () => {
          handleOpenShopify(org.data?.org.shopId);
        },
      },
    },
    {
      title: "Verify Intelligems is toggled on",
      description: "It should be on by default.",
      image: <ToggleOnScript />,
    },
    {
      title: "Test connection",
      description: `Click "Save" in Shopify, then test your connection`,
      button: testConnectionButton,
    },
  ];

  const manualFlowSteps = [
    {
      title: "Copy your script",
      description: "",
      button: {
        text: copied ? "Copied" : "Copy script",
        startIcon: <Copy size={16} />,
        onButtonClick: () => void handleCopyScript(),
      },
    },
    {
      title: "Open theme code editor in Shopify",
      description: "",
      button: {
        text: "Open Shopify",
        color: "primary" as const,
        onButtonClick: () => {
          handleOpenShopify();
          if (org.data?.org.shopId) {
            const url = generateShopifyEditorUrl({
              shopId: org.data.org.shopId,
              themeId: "current",
              redirectType: "template",
            });
            openNewTab(url);
          }
        },
      },
    },
    {
      title: "Add script to theme",
      description: (
        <IgTypography variant="body2" color={theme.palette.text.secondary}>
          Paste the Intelligems script into the &lt;head&gt; section of the
          theme.liquid file. Need help?{" "}
          <IgLink
            onClick={() =>
              openNewTab(igDirectScriptAdd, {
                mixpanelTracker: "Add Script to Theme",
              })
            }
            variant="body2"
          >
            See our guide.
          </IgLink>
        </IgTypography>
      ),
      button: testConnectionButton,
    },
  ];

  const headerTitle = scriptFailed
    ? "Hmm, something's not quite right"
    : showManualSteps
      ? "Add Intelligems script"
      : "Connect your store to get started";

  const headerSubtitle = scriptFailed
    ? "Unfortunately we can't detect our script on your store theme yet. You can retest it, or try another way to enable Intelligems."
    : showManualSteps
      ? "Follow these steps in Shopify"
      : "Complete these steps in your Shopify admin";

  const renderRightAction = () => {
    if (scriptFailed) {
      return (
        <IgButton color="primary" onClick={handleTryAnotherWay}>
          Try another way
        </IgButton>
      );
    }
    return (
      <IgButton
        color="primary"
        disabled={!continueEnabled}
        onClick={handleContinue}
      >
        Continue
      </IgButton>
    );
  };

  return (
    <Grid container direction="column" rowSpacing={6}>
      <Grid container direction="column" wrap="nowrap">
        <Grid>
          <IgTypography variant="h3" fontWeight={600}>
            {headerTitle}
          </IgTypography>
        </Grid>
        <Grid>
          <IgTypography color={theme.palette.text.secondary}>
            {headerSubtitle}
          </IgTypography>
        </Grid>
      </Grid>

      {scriptFailed ? (
        <Grid>
          <IgTypography color={theme.palette.text.secondary}>
            For more help, please{" "}
            <IgLink
              onClick={() =>
                openNewTab(allIntegrationGuides, {
                  mixpanelTracker: "Adding Intelligems Script to your Theme",
                })
              }
            >
              visit our help center
            </IgLink>{" "}
            or reach out to our support team{" "}
            <IgLink onClick={contactSupport}>here</IgLink>.
          </IgTypography>
        </Grid>
      ) : (
        <Grid>
          <StepCardsContainer
            steps={showManualSteps ? manualFlowSteps : autoFlowSteps}
          />
        </Grid>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={2} alignItems="center">
          {displaySkip && (
            <IgButton
              color="link"
              onClick={() => {
                mpScriptIntegration(currentScreenName, "Do this later");
                onSkip!();
              }}
              sx={{ whiteSpace: "nowrap" }}
            >
              Do this later
            </IgButton>
          )}
          {scriptFailed ? (
            <IgButton onClick={handleRetest}>Retest the script</IgButton>
          ) : (
            <IgButton color="link" onClick={handleToggleManualSteps}>
              {showManualSteps ? "Back" : "Add script manually"}
            </IgButton>
          )}
        </Stack>
        {renderRightAction()}
      </Stack>
    </Grid>
  );
};
